import React, { Ref, useState } from "react";
import * as RadixSelect from "@radix-ui/react-select";
import classnames from "classnames";
import styled from "styled-components";

const LocalSelectItem = React.forwardRef<HTMLDivElement, RadixSelect.SelectItemProps>(
  InnerSelectItem,
);
function InnerSelectItem(
  { children, className, ...props }: RadixSelect.SelectItemProps,
  forwardedRef: Ref<HTMLDivElement>,
): React.JSX.Element {
  return (
    <RadixSelect.Item className={classnames("SelectItem", className)} {...props} ref={forwardedRef}>
      <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
    </RadixSelect.Item>
  );
}

const StyledSelectItem = styled(LocalSelectItem)<{ $ucFirst: boolean }>`
  line-height: 1;
  display: flex;
  align-items: center;
  font-size: ${({ theme }) => theme.typography.sizeSM.toString()};
  height: 48px;
  padding: 0 16px;
  position: relative;
  user-select: none;
  color: ${({ theme }) => theme.colors.content};

  ::first-letter {
    text-transform: ${({ $ucFirst }) => ($ucFirst ? "uppercase" : "none")};
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundHover};
    border: none;
    outline: none;
  }

  &:focus {
    background-color: ${({ theme }) => theme.colors.backgroundHover};
    outline: none;
    border: none;
  }

  &[data-state="checked"] {
    color: ${({ theme }) => theme.colors.contentInverse};
    background-color: ${({ theme }) => theme.colors.backgroundAccentBlue};
    border: none;
    outline: none;
  }
`;

const SelectContent = styled(RadixSelect.Content)`
  background-color: ${({ theme }) => theme.colors.backgroundElevated3};
  overflow: hidden;
  width: var(--radix-select-trigger-width);
  max-height: var(--radix-select-content-available-height);
  border-radius: 6px;
  box-shadow:
    0px 4px 24px 0px rgba(0, 0, 0, 0.24),
    0px 2px 8px 0px rgba(0, 0, 0, 0.12);
  font-family: ${({ theme }) => theme.typography.family};
  font-size: ${({ theme }) => theme.typography.sizeSM.toString()};
  font-weight: 400;
  z-index: 110; // to be above the dialog and dialog overlay
`;

const SelectViewport = styled(RadixSelect.Viewport)`
  padding: 8px 0;
`;

const SelectIcon = styled(RadixSelect.Icon)`
  margin-left: auto;
  color: ${({ theme }) => theme.colors.contentEmphasis};
`;

const StyledSelectTrigger = styled(RadixSelect.Trigger)<{ $ucFirst: boolean }>`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.backgroundInput};
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.borderInput};
  color: ${({ theme }) => theme.colors.content};
  display: inline-flex;
  font-family: ${({ theme }) => theme.typography.family};
  font-size: ${({ theme }) => theme.typography.sizeBase.toString()};
  height: 40px;
  justify-content: center;
  line-height: 0;
  outline: none;
  padding: 8px;
  user-select: none;

  ::first-letter {
    text-transform: ${({ $ucFirst }) => ($ucFirst ? "uppercase" : "none")};
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundInput};
    border: 1px solid ${({ theme }) => theme.colors.borderInputHover};
  }

  &:focus {
    border: 1px solid ${({ theme }) => theme.colors.borderFocus};
    outline: 1px solid ${({ theme }) => theme.colors.borderFocus};
  }

  &[data-state="open"] {
    border: 1px solid ${({ theme }) => theme.colors.borderFocus};
    outline: 1px solid ${({ theme }) => theme.colors.borderFocus};
  }

  > span {
    &:first-child {
      position: relative;
      top: 1px;
    }
  }
`;

type LocalSelectTriggerProps = RadixSelect.SelectTriggerProps & {
  readonly isOpen: boolean;
  readonly iconOpen: React.ReactNode;
  readonly iconClosed: React.ReactNode;
  readonly onOpenChange?: (open: boolean) => void;
  readonly placeHolder: string;
  readonly $ucFirst: boolean;
};

function LocalSelectTrigger({
  isOpen,
  iconOpen,
  iconClosed,
  placeHolder,
  ...props
}: LocalSelectTriggerProps): React.JSX.Element {
  return (
    <StyledSelectTrigger {...props}>
      <RadixSelect.Value placeholder={placeHolder} />
      <SelectIcon>{isOpen ? iconOpen : iconClosed}</SelectIcon>
    </StyledSelectTrigger>
  );
}

export type SelectItem<T extends string> = {
  value: T;
  label: string;
};

type SelectProps<T extends string> = {
  readonly id?: string;
  readonly style?: React.CSSProperties;
  readonly items?: SelectItem<T>[];
  readonly value?: string;
  readonly onValueChange?: (value: T) => void;
  readonly iconOpen?: React.ReactNode;
  readonly iconClosed?: React.ReactNode;
  readonly placeHolder?: string;
  readonly label?: string;
  readonly ucFirst?: boolean;
};

export function Select<T extends string>({
  id,
  value,
  onValueChange = () => {},
  items,
  iconOpen = <tractie-icon name="chevron-up" />,
  iconClosed = <tractie-icon name="chevron-down" />,
  placeHolder = "Select a value",
  label,
  style,
  ucFirst = false,
}: SelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <RadixSelect.Root value={value} onOpenChange={setIsOpen} onValueChange={onValueChange}>
      <LocalSelectTrigger
        id={id}
        style={style}
        isOpen={isOpen}
        iconClosed={iconClosed}
        iconOpen={iconOpen}
        placeHolder={placeHolder}
        $ucFirst={ucFirst}
        aria-label={label}
      />
      <RadixSelect.Portal>
        <SelectContent position="popper" sideOffset={1} className="SelectContent">
          <SelectViewport>
            <RadixSelect.Group>
              {items &&
                items.map((item) => (
                  <StyledSelectItem key={item.value} value={item.value} $ucFirst={ucFirst}>
                    {item.label}
                  </StyledSelectItem>
                ))}
            </RadixSelect.Group>
          </SelectViewport>
        </SelectContent>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}
